import { AnalyticsBrowser } from "@segment/analytics-next";
import AnalyticsProxy from "@streemau/analytics-proxy";
import Logrocket from "logrocket";
import mixpanel from "mixpanel-browser";
import { storeToRefs } from "pinia";

import { debug } from "shared/constants";
import { logrocketOptions } from "shared/helpers/logrocketSanitizers";
import type { RelayItem } from "shared/services/analytics/eventRelay";
import * as eventRelay from "shared/services/analytics/eventRelay";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";
import type { Nullable } from "shared/types";

import type { App } from "./app";

interface AnalyticsOptions {
  isAdminUser?: boolean;
}

interface ProviderConfig {
  [key: string]: unknown;
}

type AnalyticsConfig = Record<string, unknown> & {
  intercom?: ProviderConfig;
  logrocket?: ProviderConfig;
  mixpanel?: ProviderConfig;
  segment?: ProviderConfig;
};

type AnalyticsProxyInstance = InstanceType<typeof AnalyticsProxy>;
type IntercomSettings = Record<string, unknown>;

let analytics: Nullable<AnalyticsProxyInstance> = null;
let identified = false;
let initialised = false;
const intercomSettings: IntercomSettings = {};

export function updateIntercom(options: Partial<IntercomSettings> = {}): void {
  Object.assign(intercomSettings, options);

  if (window.Intercom) window.Intercom("update", intercomSettings);
}

function identify(...args: unknown[]): void {
  if (!isLoggedInViaAdmin()) {
    analytics?.identify(...args);
    identified = true;

    if (debug) {
      // eslint-disable-next-line no-console
      console.log("$identify: ", ...args);
    }
  }
}

function init(): void {
  if (!initialised && analytics) {
    analytics.init();
    initialised = true;
  }
}

function isLoggedInViaAdmin(): boolean {
  const loginViaAdminMode = window.location.search.includes("login_code");
  const accountManager = sessionStorage.getItem("accountManager");

  return (
    loginViaAdminMode || Boolean(accountManager && accountManager.length > 0)
  );
}

function mountIntercom(appID: Nullable<string>): void {
  if (!appID) return;

  const jsUrl = `https://widget.intercom.io/widget/${appID}`;

  if (window.Intercom) {
    try {
      window.Intercom("shutdown");
    } finally {
      delete window.Intercom;
    }
  }

  // Below is the original function for mounting intercom with the parts that
  // mount the script tag removed, as we mount the script tag ourselves below

  /* eslint-disable */
  (function () {
    const w = window;
    const ic = w.Intercom;

    if (typeof ic === "function") {
      ic("reattach_activator");
      ic("update", intercomSettings);
    } else {
      const d = document;

      const i = function () {
        i.c(arguments);
      };

      // @ts-ignore
      i.q = [];

      // @ts-ignore
      i.c = function (args) {
        i.q.push(args);
      };

      w.Intercom = i;
    }
  })();
  /* eslint-enable */

  const script = document.querySelector('script[src*="widget.intercom.io"]');
  const parentNode = script?.parentNode;
  if (parentNode) parentNode.removeChild(script);

  const newScript = document.createElement("script");
  newScript.type = "text/javascript";
  newScript.src = jsUrl;
  document.head.appendChild(newScript);
}

export function newAnalyticsProxy(
  app: App,
  { isAdminUser = false }: AnalyticsOptions = {}
): void {
  const { $isAdminMode = false } = app.config.globalProperties;

  if (analytics) reset(); // shutdown any previous analytics

  const globalStore = useGlobalStore();
  const { appConfig, isCisionOne } = storeToRefs(globalStore);

  const { adminUserLogrocketAppId, logrocketAppId, segmentWriteKey } =
    appConfig.value;

  let { intercomAppId } = appConfig.value;

  let logrocketKey: Nullable<string> = isAdminUser
    ? adminUserLogrocketAppId
    : logrocketAppId;

  let segmentKey: Nullable<string> = segmentWriteKey;
  let mixpanelToken: Nullable<string> = import.meta.env.VITE_MIXPANEL_TOKEN;

  if (!intercomAppId) intercomAppId = null;

  if (!/^production_[a-z]{2}$/.test(import.meta.env.VITE_ENV)) {
    mixpanelToken = null;
    intercomAppId = null;
    logrocketKey = null;
    segmentKey = null;
  }

  if (import.meta.env.VITE_SEGMENT_WRITE_KEY) {
    segmentKey = import.meta.env.VITE_SEGMENT_WRITE_KEY;
  }

  if (import.meta.env.VITE_INTERCOM_APP_ID) {
    intercomAppId = import.meta.env.VITE_INTERCOM_APP_ID;
  }

  if (isAdminUser || $isAdminMode) {
    segmentKey = null;
  }

  const config: AnalyticsConfig = {};

  if (mixpanelToken) {
    config.mixpanel = {
      mixpanel,
      token: mixpanelToken,
    };

    // CisionOne data lives in the EU, so route its Mixpanel requests to
    // Mixpanel's EU servers per the data residency requirement
    if (isCisionOne.value) {
      config.mixpanel.apiHost = "https://api-eu.mixpanel.com";
    }
  }

  // eslint-disable-next-line no-param-reassign
  app.config.globalProperties.$intercomEnabled = Boolean(
    intercomAppId && !$isAdminMode
  );

  if (app.config.globalProperties.$intercomEnabled) {
    mountIntercom(intercomAppId);

    config.intercom = {
      app_id: intercomAppId,
      event_allowlist: [
        "Saw Paper Article",
        "Saw Online Article",
        "Media and Transcripts clip download requested",
        "Instant Edit Download clip requested",
        "Played Clip",
        "Read Alert",
        "Create Stream Report",
        "Built Report",
        "Feature Teaser - modal opened",
        "Feature Teaser - more info clicked",
        "Feature Teaser - request access clicked",
        "Feature Teaser - request access form submitted",
        "Content Teasers - modal opened",
        "Content Teasers - more info clicked",
        "Content Teasers - request access clicked",
        "Content Teasers - request access form submitted",
        "Content Teasers - modal closed",
        "Insights Plus Teaser - modal opened",
        "Insights Plus Teaser - get in touch form shown",
        "Insights Plus Teaser - get in touch form submitted",
        "Insights Plus Teaser - modal closed",
        "Insights Plus Teaser - example report downloaded",
        "Insights Plus Teaser - fact sheet downloaded",
        "New User Requested",
        "New Brand Profile created",
        "Opened Universal Search",
      ],
    };
  }

  if (logrocketKey) {
    config.logrocket = {
      app_id: logrocketKey,
      logrocket: Logrocket,
      options: logrocketOptions,
    };
  }

  if (segmentKey) {
    // Relay Segment track/pageview through the first-party backend endpoint,
    // which ad-blockers can't block (identify still uses the browser SDK).
    config.segment = {
      AnalyticsBrowser,
      relay: (item: RelayItem) => eventRelay.enqueue(item),
      writeKey: segmentKey,
    };
  }

  analytics = new AnalyticsProxy(config);

  if (!isLoggedInViaAdmin()) {
    init();

    if (segmentKey) eventRelay.start();
  }
}

function pageView(): void {
  if (!isLoggedInViaAdmin() && identified) {
    analytics?.pageView();
  }
}

function reset(): void {
  stop();

  init();
}

function stop(): void {
  if (!isLoggedInViaAdmin() && initialised && identified) {
    analytics?.reset();
    eventRelay.stop();
    initialised = false;
    identified = false;
  }
}

function track(...args: unknown[]): void {
  if (!isLoggedInViaAdmin() && identified) {
    analytics?.trackEvent(...args);

    if (debug) {
      // eslint-disable-next-line no-console
      console.log("$track: ", ...args);
    }
  }
}

export default async function analyticsBoot({
  app,
  router,
}: {
  app: App;
  router: { afterEach: (callback: () => void) => void };
}): Promise<void> {
  const { isAdminUser, isLoggedIn } = storeToRefs(useUserStore());
  await newAnalyticsProxy(app, { isAdminUser: isAdminUser.value });

  // eslint-disable-next-line no-param-reassign
  app.config.globalProperties.$track = track;

  if (!isLoggedInViaAdmin()) {
    // Record a page view each time the url changes
    router.afterEach(() => {
      if (isLoggedIn.value) {
        pageView();
      }
    });
  }
}

export { identify, reset, track };
